import { getNumberFormatter } from '@genesislcap/foundation-utils';
import { ExecutionContext, html, ref, repeat, when, whenElse } from '@genesislcap/web-core';
import { classNames } from '@microsoft/fast-web-utilities';
import { IconStatusBadge } from '../components/icon-status-badge';
import { DealStatusEnum } from '../deals.types';
import { getDisplayDealStatus, isBofaLead } from '../deals.util';
import { GreyButton } from '../components/grey-button';
import type { DealDetail } from './deal-detail';
import { formatDateOrBlank } from './deal-detail.util';
import { DealDetailMetadata } from './deal-detail-metadata';
import { BondSeries, SeriesWithOrders } from './deal-detail.types';
import { DealSeriesTabInfoComponent } from './deal-series-tab-info.component';
import { DealSubmitPreview } from './deal-submit-preview/deal-submit-preview';
import { customEvent } from '@genesislcap/foundation-events';
import { SyndicateInfo } from '../syndicate-info';
import { HIGHLIGHT_AMBER_BG, HIGHLIGHT_AMBER_COLOR } from '../../../styles';
import { RECONFIRM_MODAL } from '../deal-series/deal-series.constants';
import { isOrderPeriodDatesApplicable } from '../deal-series/deal-status';

const currencyFormatter = getNumberFormatter('$0,0');

DealDetailMetadata;
IconStatusBadge;
GreyButton;
DealSubmitPreview;
DealSeriesTabInfoComponent;
SyndicateInfo;

export const dealDetailTemplate = html<DealDetail>`
  ${when(
    (x) => x.munisDeal,
    html<DealDetail>`
      <div class="detail-header">
        <div class="detail-header-item description">
          <div class="detail-header-item-description">
            ${(x) => currencyFormatter({ value: x.munisDeal.DEAL_SIZE })} | ${(x) => x.munisDeal.DEAL_DESCRIPTION}
          </div>
          ${when(
            (x) => x.bondSeriesList?.length > 1,
            html<DealDetail>`
              ${repeat(
                (x) => x.bondSeriesList,
                html<BondSeries>`
                  <div class="detail-header-item-subline">
                    • ${(x) => x.SERIES_DESCRIPTION}
                  </div>
                `
              )}
            `,
          )}
        </div>
        <div class="flex-spacer"></div>
        ${when(
          (x) =>
            (x.munisDeal.DEAL_TYPE === 'Negotiated' || x.munisDeal.DEAL_TYPE === 'Competitive') &&
            x.munisDeal.EXPECTED_PRICING_DATE != null,
          html<DealDetail>`
            <div class="detail-header-item detail-header-date-field">
              <div class="label">${(x) =>
                x.munisDeal.DEAL_TYPE === 'Negotiated' ? 'Pricing Date:' : 'Sale Date (Time):'}</div>
              <div class="detail-header-date-value">${(x) =>
                formatDateOrBlank(
                  x.munisDeal.EXPECTED_PRICING_DATE,
                  x.munisDeal.DEAL_TYPE === 'Competitive',
                )}</div>
            </div>
          `,
        )}
        ${when(
          (x) => x.munisDeal.DEAL_STATUS !== DealStatusEnum.NONE,
          html<DealDetail>`
            <div class="detail-header-item detail-header-badge-field">
              <div class="label">Status:</div>
              <icon-status-badge
                background-color="rgba(135, 155, 166, 0.2)"
                large
                badge-text="${(x) => {
                  const tradeDate = x.bondSeriesList?.[0]?.TRADE_DATE;
                  const dealType = x.munisDeal.DEAL_TYPE;
                  return getDisplayDealStatus(x.munisDeal.DEAL_STATUS, dealType, tradeDate ? new Date(tradeDate).toISOString() : null);
                }}"
              ></icon-status-badge>
            </div>
          `,
        )}
        ${when(
          (x) => x.munisDeal.WIRE_TYPE,
          html<DealDetail>`
            <div class="detail-header-item detail-header-badge-field">
              <div class="label">Wire:</div>
              <icon-status-badge
                class="wire-type-badge"
                large
                badge-text="${(x) => x.munisDeal.WIRE_TYPE}"
              ></icon-status-badge>
            </div>
          `,
        )}
        <div class="detail-header-item">
          <div class="detail-header-lead">
            <div class="detail-header-lead-badge">
              ${whenElse(
                (x) => isBofaLead(x.munisDeal.SYNDICATE),
                html<DealDetail>`
                  <img alt="Bank of America logo" src="/logos/bofa-white.svg" />
                `,
                html<DealDetail>`
                  ${(x) => x.munisDeal.SYNDICATE.substring(0, 1).toUpperCase()}
                `,
              )}
            </div>
            <div class="detail-header-indication-label">
              ${(x) => x.munisDeal.SYNDICATE?.split(';')[0] || x.munisDeal.SYNDICATE}
              <div class="label">Lead</div>
              <syndicate-info
                :deal="${(x) => x.munisDeal}">
              </syndicate-info>
            </div>
          </div>
        </div>
      </div>
      <div
        data-test-id="toggle-information-panel"
        @click="${(x) => x.toggleInformationPanel()}"
        class="detail-metadata-toggle">
        <div class="detail-metadata-toggle-button ${(x) => classNames(['open', x.informationPanelActive])}"
        ">
        <img alt="Toggle metadata" src="/deal-icons/chevron-right-double.svg">
      </div>
    </div>
    ${when(
      (x) => x.informationPanelActive,
      html<DealDetail>`
        <deal-detail-metadata
          data-test-id="deal-metadata"
          :munisDeal="${(x) => x.munisDeal}"
          :bondSeriesList="${(x) => x.bondSeriesList}"
          :purposeMappings="${(x) => x.purposeMappings}"
        ></deal-detail-metadata>
        <rapid-divider></rapid-divider>
      `,
    )}
    <div class="detail-controls">
      <div class="detail-controls-entry">
        <div><b>Order Entry</b></div>
      </div>
      ${when(
        (x) =>
          isOrderPeriodDatesApplicable(x.munisDeal.DEAL_STATUS) &&
          x.munisDeal.ORDER_PERIOD_END_DATE !== null &&
          x.munisDeal.ORDER_PERIOD_BEGIN_DATE !== null,
        html<DealDetail>`
          <div class="detail-controls-item">
            <rapid-status-pill
              icon="clock"
              text="Closes:"
              background-color="${HIGHLIGHT_AMBER_BG}"
              text-color="${HIGHLIGHT_AMBER_COLOR}"
              date-countdown=${(x) => x.munisDeal.ORDER_PERIOD_END_DATE}
              date-countdown-visible-after=${(x) => x.munisDeal.ORDER_PERIOD_BEGIN_DATE}
              hide-when-countdown-completed=""
            >
            </rapid-status-pill>
          </div>
        `,
      )}
      <div class="detail-header-actions">
        <rapid-button
          ?disabled="${(x) => !x.reconfirmEnabled}"
          @click="${(x) => x.showReconfirmModal()}"
          appearance="primary">
          Reconfirm All Orders
        </rapid-button>
        <rapid-button
          ?disabled="${(x) => !x.submitEnabled || x.reconfirmEnabled}"
          @click="${(x) => x.previewSubmitOrders()}" 
          appearance="primary">
          Review and Submit Orders
        </rapid-button>
        <rapid-button
          appearance="danger"
          @click="${(x) => x.reviewCancelOrders()}"
          ?disabled="${(x) => !x.cancelEnabled}">
          Cancel Orders
        </rapid-button>
      </div>
    </div>
    <div class="detail-container">
      ${when(
        (x) => x.bondSeriesList?.length === 0,
        html<SeriesWithOrders>`
          <div class="detail-container-empty">
            Series and Maturity Data is not yet available for this Deal
          </div>
        `,
      )}
      ${when(
        (x) => x.bondSeriesList?.length === 1,
        html<DealDetail>`
          <!--To DO add title section for single series -->
          <deal-series
            @orders-changed="${(x, ctx) => x.handleOrdersChanged(customEvent(ctx))}"
            :series="${(x) => x.bondSeriesList[0]}"
            :bondSeriesList="${(x) => x.bondSeriesList}"
            :flexMaturityOptions="${(x) => x.flexMaturityOptions}"
            :totalDealQuantity="${(x) => x.totalDealQuantity}"
            ?deal-has-eligible-orders-to-reset="${(x) => x.dealHasEligibleOrdersToReset}"
            call-feature-description="${(x) =>
              x.bondSeriesList[0].CALL_FEATURE_DESCRIPTION}"
            deal-status="${(x) => x.munisDeal.DEAL_STATUS}"
          ></deal-series>
        `,
      )}
      ${when(
        (x) => x.bondSeriesList?.length > 1,
        html<DealDetail>`
          <rapid-tabs
            @change=${(x, ctx) => x.handleTabChanged(customEvent(ctx))}
            appearance="secondary">
            ${repeat(
              (x) => x.bondSeriesList,
              html<BondSeries>`
                <rapid-tab id="${x => x.SERIES_ID}">
                  <deal-series-tab-info
                    :bondSeries="${(x) => x}"
                    ?is-active="${(x, ctx: ExecutionContext<DealDetail>) => x.SERIES_ID === ctx.parent.activeTabId}"
                  ></deal-series-tab-info>
                </rapid-tab>
              `,
              { positioning: true },
            )}
            ${repeat(
              (x) => x.bondSeriesList,
              html<BondSeries>`
                <rapid-tab-panel>
                  <deal-series
                    @orders-changed="${(x, ctx: ExecutionContext<DealDetail>) => ctx.parent.handleOrdersChanged(customEvent(ctx))}"
                    call-feature-description="${(x) => x.CALL_FEATURE_DESCRIPTION}"
                    :series="${(x) => x}"
                    :bondSeriesList="${(x, ctx: ExecutionContext<DealDetail>) => ctx.parent.bondSeriesList}"
                    :flexMaturityOptions="${(x, ctx: ExecutionContext<DealDetail>) => ctx.parent.flexMaturityOptions}"
                    :totalDealQuantity="${(x, ctx: ExecutionContext<DealDetail>) => ctx.parent.totalDealQuantity}"
                    ?deal-has-eligible-orders-to-reset="${(x, ctx: ExecutionContext<DealDetail>) => ctx.parent.dealHasEligibleOrdersToReset}"
                    deal-status="${(x, ctx: ExecutionContext<DealDetail>) => ctx.parent.munisDeal.DEAL_STATUS}"
                  ></deal-series>
                </rapid-tab-panel>
              `,
              { positioning: true },
            )}
          </rapid-tabs>
        `,
      )}
    </div>
    <rapid-modal
      :onCloseCallback="${(x) => () => x.closeSubmitOrders()}"
      class="submit-modal" 
      ${ref('previewOrdersModal')}>
      <h3 slot="top">Review and Submit Orders</h3>
      <deal-submit-preview
        ${ref('submitPreview')}
        :deal-status="${(x) => x.munisDeal?.DEAL_STATUS ?? null}"
        :munisDeal="${(x) => x.munisDeal ?? null}"
        ?in-reprice-window="${(x) => x.isInRepriceWindow()}"
        :submitOrdersBySeriesId="${(x) => x.submitOrdersBySeriesId}"
        :submitOrdersVersion="${(x) => x.submitOrdersVersion}"
        :ordersBySeriesId="${(x) => x.ordersBySeriesId}"
        :bondSeriesList="${(x) => x.bondSeriesList}"
        @close="${(x) => x.closeSubmitOrders()}"
        @submit-success="${(x) => x.handleSubmitSuccess(x.deal.DEAL_ID)}"
      ></deal-submit-preview>
      </rapid-modal>
      <rapid-modal
        :onCloseCallback="${(x) => () => x.closeReviewCancelOrdersModal()}"
        class="submit-modal"
        ${ref('reviewCancelOrdersModal')}>
        <h3 slot="top">Cancel Orders</h3>
        <deal-submit-preview
          ${ref('cancelSubmitPreview')}
          cancellation
          :deal-status="${(x) => x.munisDeal?.DEAL_STATUS ?? null}"
          :munisDeal="${(x) => x.munisDeal ?? null}"
          ?in-reprice-window="${(x) => x.isInRepriceWindow()}"
          ?cancel-only-affected-orders="${(x) => x.isInRepriceWindow()}"
          :submitOrdersBySeriesId="${(x) => x.submitOrdersBySeriesId}"
          :submitOrdersVersion="${(x) => x.submitOrdersVersion}"
          :ordersBySeriesId="${(x) => x.ordersBySeriesId}"
          :bondSeriesList="${(x) => x.bondSeriesList}"
          @close="${(x) => x.closeReviewCancelOrdersModal()}"
          @submit-success="${(x) => x.handleSubmitSuccess(x.deal.DEAL_ID)}"
        ></deal-submit-preview>
      </rapid-modal>
      <rapid-modal
        :onCloseCallback="${(x) => () => x.closeReconfirmModal()}"
        class="submit-modal"
        ${ref('reconfirmOrdersModal')}>
        <h3 slot="top">${() => RECONFIRM_MODAL.TITLE}</h3>
        ${when(
          (x) => x.reconfirmActive,
          html<DealDetail>`
            <div class="reconfirm-modal-content">
              <p>${() => RECONFIRM_MODAL.BODY_1}</p>
              <div class="reconfirm-modal-actions">
                <rapid-button 
                  @click="${(x) => x.closeReconfirmModal()}"
                  class="reconfirm-cancel-button">
                  ${() => RECONFIRM_MODAL.BUTTON_CANCEL}
                </rapid-button>
                <rapid-button
                  @click="${(x) => x.reconfirmAllOrders()}"
                  appearance="primary">
                  ${() => RECONFIRM_MODAL.BUTTON_CONFIRM}
                </rapid-button>
              </div>
            </div>
          `
        )}
      </rapid-modal>
    `,
  )}
`;
